import {PrivilegedSiteComponent, SiteSettings} from '../base';
import {onCabinetLoad, onCalibrationLoad, onPremisesLoad} from './loaders';
import {hasComponentAccess} from "../account";
import * as Locale from '../locale';


// External links used to connect to legacy frontend
export const ACCOUNT_ROUTE_LOGIN = SiteSettings.uri.main + '/accounts/login/';
export const ACCOUNT_ROUTE_ADMIN = SiteSettings.uri.main + '/admin/';
export const ACCOUNT_ROUTE_USER_MANAGEMENT = SiteSettings.uri.keycloak + '/admin/';
export const CABINET_ROUTE_LISTING = SiteSettings.uri.main + '/console/cabinet-details/';
// todo: Make these actual links in the AppRoute
export const ACCOUNT_ROUTE_PASSWORD = '#/account/password/';
export const ACCOUNT_ROUTE_SETTINGS = '#/account/settings/';
export const CABINETS_ROUTE_LISTING = `${SiteSettings.uri.main}/console/cabinets/`;
export const REGISTRATION_ROUTE_LISTING = '#/premises/register/';

/**
 * Optional arguments for initialising an app route e.g. is the route external?
 */
export interface AppRouteOptionalArgs {
    // handler invoked when route is loaded
    onLoad?: (store, params, query) => void;
    // is the route for an external link or internal to the SPA?
    // todo: we have to duplicate the logic between model and
    // view here, work out better way
    external?: boolean;
    // designated site component for the route, if any
    component?: PrivilegedSiteComponent;
}

/**
 * Defines a route in the application e.g. stores page, dashboard, etc.
 * Each route has a unique name, with a URI path and a displayable Label for
 * the route.
 */
export class AppRoute {
    path: string;
    label: string;
    name: string;
    component: PrivilegedSiteComponent;
    external: boolean;
    onLoad: (store, params, query) => void;

    public visibleForUser(user: any) : boolean {
        // default to displaying every route for all users unless explicitly disabled
        return this.component !== undefined ? hasComponentAccess(user, this.component) : true;
    }

    constructor(name: string, path: string, label: string,
                props: AppRouteOptionalArgs = {}) {
        this.name = name;
        this.label = label;
        this.path = path;
        this.component = props.component;
        this.external = props.external || false;
        // tslint:disable-next-line:no-empty
        this.onLoad = props.onLoad || function(store, params, query) {};
    }
}

/**
 * All defined routes for the application
 */
class AppRoutes {
    dashboard: AppRoute;
    premises: AppRoute;
    calibration: AppRoute;
    alarms: AppRoute;
    configuration: AppRoute;
    cabinets: AppRoute;
    react_cabinets: AppRoute;
    alarmConfig: AppRoute;
    cabinetDetails: AppRoute;
    routes: Array<AppRoute>;

    constructor() {
        this.dashboard = new AppRoute(
            'dashboard',
            `${SiteSettings.uri.main}/console/dashboard/`,
            Locale.TEXT_NAVIGATION_DASHBOARD,
            {external: true}
        );
        this.premises = new AppRoute(
            'stores', 'stores',
            Locale.TEXT_NAVIGATION_STORES,
            {onLoad: onPremisesLoad}
        );
        this.alarms = new AppRoute(
            'alarms',
            `${SiteSettings.uri.main}/console/alarms/`,
            Locale.TEXT_NAVIGATION_ALARMS,
            {external: true}
        );
        this.configuration = new AppRoute(
            'configuration',
            `${SiteSettings.uri.main}/console/configuration/users/`,
            Locale.TEXT_NAVIGATION_CONFIGURATION,
            {external: true, component: PrivilegedSiteComponent.CabinetPermissions}
        );
        this.cabinets = new AppRoute(
            'cabinets',
            `${SiteSettings.uri.main}/console/cabinets/`,
            Locale.TEXT_NAVIGATION_CABINETS,
            {external: true}
        );
        this.react_cabinets = new AppRoute(
            'cabinets',
            'cabinets',
            Locale.TEXT_NAVIGATION_CABINETS,
            {onLoad: onCabinetLoad}
        );
        this.alarmConfig = new AppRoute(
            'manage-alarms',
            `${SiteSettings.uri.main}/alarm/manage/`,
            Locale.TEXT_NAVIGATION_MANAGE_ALARMS,
            {external: true}
        );
        this.cabinetDetails = new AppRoute(
            'cabinet-details',
            `${SiteSettings.uri.main}/console/cabinet-details/`,
            Locale.TEXT_NAVIGATION_CABINETS ,
            {external: true}
        );
        this.calibration = new AppRoute(
            'calibration', 'calibration',
            Locale.TEXT_NAVIGATION_CALIBRATION,
            {onLoad: onCalibrationLoad, component: PrivilegedSiteComponent.Calibration}
        );
        this.routes = [this.alarms, this.configuration, this.cabinetDetails, this.calibration,
                       this.premises, this.dashboard, this.alarmConfig];
    }

    byRoute(route) : AppRoute {
        let appRoute;
        if (route && route.name) {
            let foundRoutes = this.routes.filter(function(foundRoute: AppRoute) {
                return foundRoute.name === route.name;
            });
            appRoute = foundRoutes[0];
        }
        return appRoute;
    }
}

export let appRoutes = new AppRoutes();

/**
 * Routes as displayed in the main navigation bar
 */
export let navigationAppRoutes = [
    appRoutes.dashboard, appRoutes.premises, appRoutes.alarms,
    appRoutes.configuration, appRoutes.calibration
];
